import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { emailRegex } from '@app/@core/services/auth.service';
import { PhoneService } from '@app/@core/services/phone.service';
import {
  AuthenticationService,
  LegalDocument,
  LoginResponse,
  TermsAndConditionsResponse,
} from '@app/auth/authentication.service';
import { noSpecialCharactersValidator } from '@app/profile/profile-details/profile-details.component';
import { environment } from '@env/environment';
import moment from 'moment';
import { finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { interval, Subscription } from 'rxjs';
import { CoreService } from '@app/@core/services/core.service';
import {
  matchControlValidator,
  PASSWORD_RULES,
  passwordRulesValidator,
} from '@app/@shared/utils/auth-validators';
@Component({
  selector: 'app-register-basic-info',
  templateUrl: './basic-info.component.html',
  styleUrls: ['./basic-info.component.scss'],
  standalone: false,
})
export class BasicInfoComponent implements OnInit {
  step = 1;
  isLoading = false;
  isTimerActive = false;
  timer: number;
  timerSubscription: Subscription;
  registrationForm: FormGroup;
  emailMask = '';
  phoneMask = '';
  showPassword: boolean = false;
  showConfirmPassword = false;
  passwordRules = PASSWORD_RULES;
  // from environment
  countryCode = environment.countrySettings.countryCode;
  minAge = environment.countrySettings.minAgeForAccountOpening || 18;
  tooYoung = environment.countrySettings.minAgeForAccountOpening || 18;
  maxDate: Date;
  defaultBirthDate = new Date(2000, 0, 1);
  otpConfig = { length: 6, allowNumbersOnly: true };
  error: string | undefined;
  // loaded but not shown—sent on final call
  termsAndConditions: LegalDocument;
  privacyPolicy: LegalDocument;
  token: string;
  constructor(
    private fb: FormBuilder,
    private authService: AuthenticationService,
    private phoneService: PhoneService,
    private coreService: CoreService,
    private translateService: TranslateService,
  ) {
    // set max DOB
    this.maxDate = new Date();
    this.maxDate.setFullYear(this.maxDate.getFullYear() - this.minAge);
  }

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.loadTnC();
    this.buildForm();
  }

  private loadTnC() {
    this.authService
      .getTermsAndConditions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: TermsAndConditionsResponse) => {
        this.privacyPolicy = res.privacyPolicy;
        this.termsAndConditions = res.termsAndConditions;
      });
  }

  private buildForm() {
    this.registrationForm = this.fb.group({
      basic: this.fb.group({
        firstName: ['', [Validators.required, noSpecialCharactersValidator()]],
        lastName: ['', [Validators.required, noSpecialCharactersValidator()]],
        birthDate: [
          '',
          [Validators.required, this.minAgeValidator(this.minAge)],
        ],
        email: ['', [Validators.required, Validators.pattern(emailRegex)]],
        termsAccepted: [false, Validators.requiredTrue],
      }),
      emailOtp: this.fb.group({
        otp: ['', [Validators.required, Validators.minLength(6)]],
      }),
      phone: this.fb.group({
        phone: ['', [Validators.required, this.phoneValidator()]],
      }),
      smsOtp: this.fb.group({
        otp: ['', [Validators.required, Validators.minLength(6)]],
      }),
      passwordSection: this.fb.group({
        password: ['', [Validators.required, passwordRulesValidator]],
        confirm: ['', [Validators.required, matchControlValidator('password')]],
      }),
    });

    this.registrationForm
      .get('passwordSection.password')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.registrationForm
          .get('passwordSection.confirm')!
          .updateValueAndValidity(),
      );
  }

  // ────────────────────────────────────────────────────────────────────
  // STEP 1: Send Email OTP
  nextFromBasic() {
    if (this.registrationForm.get('basic')!.invalid) return;
    const { email } = this.registrationForm.get('basic')!.value;
    this.isLoading = true;
    this.authService
      .sendEmailOtp({ email })
      .pipe(
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        (response) => {
          this.emailMask = email;
          this.step = 2;
          this.setTimer(response?.expiryTime);
        },
        (err) => {
          this.coreService.showErrorDialog(err.error?.message);
        },
      );
  }

  // STEP 2: Verify Email OTP
  verifyEmail() {
    if (this.registrationForm.get('emailOtp')!.invalid) return;
    const otp = this.registrationForm.get('emailOtp.otp')!.value;
    const { email } = this.registrationForm.get('basic')!.value;
    this.isLoading = true;
    this.authService
      .verifyEmailOtp({ email, otp })
      .pipe(
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        () => {
          this.step = 3;
          this.stopTimer(); // stop timer after email verification
        },
        (err) => {
          this.coreService.showErrorDialog(err.error?.message);
        },
      );
  }

  // STEP 3: Send SMS OTP
  nextFromPhone() {
    if (this.registrationForm.get('phone')!.invalid) return;
    let phone: string = this.registrationForm.get('phone.phone')!.value;
    if (!phone.startsWith('+')) phone = `+${phone}`;
    this.isLoading = true;
    this.authService
      .sendSmsOtp({ phone })
      .pipe(
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        (response) => {
          // mask it e.g. +123••••99
          this.phoneMask = phone.replace(/(\d{3})\d+(\d{2})/, '$1••••••$2');
          this.registrationForm.get('phone')!.patchValue({ phone });
          this.step = 4;
          this.setTimer(response?.expiryTime);
        },
        (err) => {
          this.coreService.showErrorDialog(err.error?.message);
        },
      );
  }

  // STEP 4: Verify SMS OTP
  verifySms() {
    if (this.registrationForm.get('smsOtp')!.invalid) return;
    const otp = this.registrationForm.get('smsOtp.otp')!.value;
    const phone = this.registrationForm.get('phone.phone')!.value;
    this.isLoading = true;
    this.authService
      .verifySmsOtp({ phone, otp })
      .pipe(
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        (res) => {
          this.step = 5;
          this.token = res.token;
          this.stopTimer(); // stop timer after SMS verification
        },
        (err) => {
          this.coreService.showErrorDialog(err.error?.message);
        },
      );
  }

  // STEP 5: Set Password & Create Profile
  finishRegistration() {
    if (this.registrationForm.get('passwordSection')!.invalid) return;
    const basic = this.registrationForm.get('basic')!.value;
    const dobUnix = moment(basic.birthDate).valueOf();
    const phone = this.registrationForm.get('phone.phone')!.value;
    const password = this.registrationForm.get(
      'passwordSection.password',
    )!.value;

    const payload = {
      firstName: basic.firstName,
      lastName: basic.lastName,
      dateofBirthUnix: dobUnix,
      email: basic.email,
      phone,
      password,
      acceptedTncVersion: this.termsAndConditions.version,
    };

    this.isLoading = true;
    this.authService
      .createProfile(payload, this.token)
      .pipe(
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        (res) => {
          this.login(basic.email, password);
        },
        (err) => {
          this.coreService.showErrorDialog(err.error?.message);
        },
      );
  }

  private login(email: string, password: string) {
    this.authService
      .login({
        username: email,
        password: password,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.saveCredentials(res, email);
        },
        (err) => {
          this.coreService.showErrorDialog(err.error?.message);
        },
      );
  }

  private saveCredentials(credentials: LoginResponse, email: string) {
    this.authService.setCredentials({
      username: email,
      token: credentials.access_token,
      companyId: '0',
      refreshToken: credentials.refresh_token,
      companyName: undefined,
    });
    window.location.href = '/dashboard';
  }

  resendEmailOtp(
    { email }: { email: string } = {
      email: this.registrationForm.get('basic.email').value,
    },
    successCB = () => {},
  ) {
    this.isLoading = true;

    this.authService
      .sendEmailOtp({ email })
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        (response) => {
          successCB();
          this.setTimer(response?.expiryTime);
        },
        (err) => {
          this.coreService.showErrorDialog(err.error?.message);
        },
      );
  }

  resendSmsOtp({ phone }: { phone?: string } = {}, successCB = () => {}) {
    this.isLoading = true;
    let phoneNumber = phone || this.registrationForm.get('phone.phone')!.value;
    if (('' + phoneNumber).charAt(0) !== '+') {
      phoneNumber = `+${phoneNumber}`;
    }
    this.authService
      .sendSmsOtp({ phone })
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        (response) => {
          successCB();
          this.setTimer(response?.expiryTime);
        },
        (err) => {
          this.coreService.showErrorDialog(err.error?.message);
        },
      );
  }

  setTimer(expiryTime: number) {
    const expiryTimeInMilliSeconds = expiryTime - Date.now(); // Extract the expiry time from the response
    let expiryTimeInSeconds = 60;
    if (expiryTimeInMilliSeconds > 0) {
      expiryTimeInSeconds = Math.floor(expiryTimeInMilliSeconds / 1000);
    }
    this.startTimer(expiryTimeInSeconds);
  }

  startTimer(duration: number) {
    this.isTimerActive = true;
    this.timer = duration;
    this.timerSubscription = interval(1000).subscribe(() => {
      this.timer--;
      if (this.timer === 0) {
        this.stopTimer();
      }
    });
  }

  getTimerText(sec: number) {
    if (sec > 60) {
      const minutes = Math.floor(sec / 60);
      const seconds = sec % 60;
      return `${minutes} ${this.translateService.instant(
        'minute(s)',
      )} ${this.translateService.instant(
        'and',
      )} ${seconds} ${this.translateService.instant('second(s)')}`;
    }
    return `${sec} ${this.translateService.instant('second(s)')}`;
  }

  stopTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    this.isTimerActive = false;
  }

  // ────────────────────────────────────────────────────────────────────
  private minAgeValidator(min: number): ValidatorFn {
    return (c: AbstractControl) => {
      if (!c.value) return null;
      const age = moment().diff(moment(c.value), 'years');
      return age >= min
        ? null
        : { minAge: { requiredAge: min, actualAge: age } };
    };
  }

  private phoneValidator(): ValidatorFn {
    return (c: AbstractControl) => {
      try {
        const v = c.value.startsWith('+') ? c.value : `+${c.value}`;
        if (!this.phoneService.isValidNumber(v)) return { badPhone: true };
      } catch {
        return { badPhone: true };
      }
      return null;
    };
  }

  showHidePassword() {
    this.showPassword = !this.showPassword;
  }

  showHideConfirmPassword() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }
}
